import React from 'react';
import { useNavigate } from 'react-router';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { FundSummary } from '@/types/fund';
import { usePrefetchFund } from '@/hooks/react-query/usePrefetch';
import { ChildFundRow } from '@/components/funds/ChildFundRow';
import {
  getFundStatusAppearance,
  getFundTypeDisplayName,
  getFundStatusDisplayName,
} from '@/services/funds/displayService';
import { formatCurrencyNoDecimals } from '@/utils/formatters';

interface FundsListProps {
  funds: FundSummary[];
  expandedFunds: Set<string>;
  onToggleFundExpansion: (fundId: string) => void;
  commitmentTotals: Record<string, number>;
}

export function FundsList({
  funds,
  expandedFunds,
  onToggleFundExpansion,
  commitmentTotals,
}: FundsListProps) {
  const navigate = useNavigate();
  const prefetchFund = usePrefetchFund();

  const mainFunds = funds.filter((fund) => !fund.parent_fund_id);

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Fund Name
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Vintage
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Currency
              </th>
              <th className="px-6 py-2 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Commitment Amount
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {mainFunds.map((mainFund) => {
              const childFunds = funds.filter(
                (f) => f.parent_fund_id === mainFund.id
              );
              const hasChildren = childFunds.length > 0;
              const isExpanded = expandedFunds.has(mainFund.id);
              const mainFundCommitment = commitmentTotals[mainFund.id] ?? 0;

              return (
                <React.Fragment key={mainFund.id}>
                  {/* Main Fund Row */}
                  <tr
                    className="hover:bg-accent cursor-pointer"
                    onClick={() => navigate(`/app/funds/${mainFund.id}`)}
                    onMouseEnter={() => prefetchFund(mainFund.id)}
                  >
                    <td className="px-6 py-2 whitespace-nowrap">
                      <div className="flex items-center">
                        {hasChildren && (
                          <button
                            className="mr-2 p-1 hover:bg-accent rounded"
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleFundExpansion(mainFund.id);
                            }}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </button>
                        )}
                        {!hasChildren && <div className="w-6" />}
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {mainFund.display_name || mainFund.name}
                          </div>
                          {hasChildren && (
                            <div className="text-xs text-muted-foreground">
                              {childFunds.length} feeder fund
                              {childFunds.length !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap">
                      <span className="text-sm text-foreground">
                        {getFundTypeDisplayName(mainFund.fund_type)}
                      </span>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getFundStatusAppearance(mainFund.status)}`}
                      >
                        {getFundStatusDisplayName(mainFund.status)}
                      </span>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-foreground">
                      {mainFund.vintage_year}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-foreground">
                      {mainFund.base_currency}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-foreground">
                        {formatCurrencyNoDecimals(
                          mainFundCommitment,
                          mainFund.base_currency
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Child Fund Rows */}
                  {hasChildren &&
                    isExpanded &&
                    childFunds.map((childFund) => (
                      <ChildFundRow
                        key={childFund.id}
                        childFund={childFund}
                        commitmentAmount={commitmentTotals[childFund.id] ?? 0}
                      />
                    ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
